import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { TranslateService } from '@core/services/translate.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface OverflowNavItem {
  key: string;
  route: string;
  icon: string;
  labelKey: string;
  labelFallback: string;
}

/**
 * Horizontal primary nav that adapts to available width using the
 * "Priority+" pattern: items that don't fit are moved into a "More"
 * dropdown so they stay reachable at every viewport size.
 */
@Component({
  selector: 'app-overflow-nav',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    NzIconModule,
    NzDropDownModule,
    NzMenuModule,
    NzButtonModule,
    TranslatePipe,
  ],
  templateUrl: './overflow-nav.component.html',
  styleUrl: './overflow-nav.component.scss',
})
export class OverflowNavComponent implements AfterViewInit, OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  readonly items = input.required<OverflowNavItem[]>();
  readonly dotKeys = input<string[]>([]);
  readonly badgeCounts = input<Record<string, number>>({});
  readonly ariaLabel = input<string>('Primary navigation');

  readonly itemClick = output<OverflowNavItem>();

  protected readonly visibleCount = signal<number>(Number.MAX_SAFE_INTEGER);
  protected readonly visibleItems = computed(() => this.items().slice(0, this.visibleCount()));
  protected readonly overflowItems = computed(() => this.items().slice(this.visibleCount()));
  protected readonly hasOverflow = computed(() => this.overflowItems().length > 0);

  private readonly container = viewChild<ElementRef<HTMLElement>>('container');
  private readonly measureList = viewChild<ElementRef<HTMLElement>>('measureList');
  private readonly moreBtn = viewChild<ElementRef<HTMLElement>>('moreBtn');
  protected readonly measureItems = viewChildren<ElementRef<HTMLElement>>('measureItem');

  private resizeObserver?: ResizeObserver;
  private scheduled = false;

  constructor() {
    // Recompute whenever the items array changes.
    effect(() => {
      // Access signal to register dependency.
      this.items();
      this.scheduleRecompute();
    });

    // Recompute when translations change (label widths may change).
    this.translate.translations$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scheduleRecompute());
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRecompute());
        const containerEl = this.container()?.nativeElement;
        const measureEl = this.measureList()?.nativeElement;
        if (containerEl) this.resizeObserver.observe(containerEl);
        if (measureEl) this.resizeObserver.observe(measureEl);
      }
    });

    // Initial measurement after fonts are ready (label widths stabilize).
    const fonts = (typeof document !== 'undefined' ? (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts : undefined);
    if (fonts?.ready) {
      fonts.ready.then(() => this.scheduleRecompute());
    }
    this.scheduleRecompute();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  protected onVisibleClick(item: OverflowNavItem): void {
    this.itemClick.emit(item);
  }

  protected onOverflowClick(item: OverflowNavItem): void {
    this.itemClick.emit(item);
  }

  protected hasDot(item: OverflowNavItem): boolean {
    return this.dotKeys().includes(item.key);
  }

  protected badgeCount(item: OverflowNavItem): number {
    const count = this.badgeCounts()[item.key] ?? 0;
    return count > 0 ? count : 0;
  }

  protected badgeLabel(item: OverflowNavItem): string {
    const count = this.badgeCount(item);
    if (count <= 0) {
      return '';
    }

    return count > 99 ? '99+' : String(count);
  }

  protected trackByKey(_: number, item: OverflowNavItem): string {
    return item.key;
  }

  private scheduleRecompute(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    // Defer to next frame to let layout settle.
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.scheduled = false;
        this.zone.run(() => this.recompute());
      });
    });
  }

  private recompute(): void {
    const containerEl = this.container()?.nativeElement;
    const measureEl = this.measureList()?.nativeElement;
    if (!containerEl || !measureEl) return;
    const itemEls = this.measureItems().map((ref) => ref.nativeElement);
    if (itemEls.length === 0) {
      this.visibleCount.set(0);
      return;
    }

    const containerWidth = containerEl.clientWidth;
    if (containerWidth <= 0) {
      // Not yet rendered; retry next frame.
      this.scheduleRecompute();
      return;
    }

    const style = getComputedStyle(measureEl);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;

    const widths = itemEls.map((el) => el.offsetWidth);
    const totalAll = widths.reduce((a, b) => a + b, 0) + gap * Math.max(0, widths.length - 1);

    if (totalAll <= containerWidth) {
      this.visibleCount.set(widths.length);
      return;
    }

    // Reserve space for the "More" button.
    const moreWidth = this.moreBtn()?.nativeElement.offsetWidth ?? 80;
    const budget = Math.max(0, containerWidth - moreWidth - gap);

    let used = 0;
    let count = 0;
    for (const width of widths) {
      const add = width + (count > 0 ? gap : 0);
      if (used + add > budget) break;
      used += add;
      count += 1;
    }

    // Ensure at least one item is visible if the container is wide enough.
    if (count === 0 && widths[0] <= containerWidth) {
      count = 1;
    }

    this.visibleCount.set(count);
  }
}
